/* UI */
import { Box, Card } from '@auviknetworks/iceberg-ui';
import { Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import {
    AppNameLinkCell,
    DateFormatCell,
    HMSCell,
    UserBubblesCell
} from 'application/Shared/Components/Cells';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { ColumnDef } from 'application/Shared/Components/Table/types';
import { TABLENAMES } from 'application/Shared/Constants';
import { hmsFormatter } from 'application/Shared/Helpers';
import { Application } from 'application/Shared/types/application';
import { User } from 'application/Shared/types/user';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

interface UsageVsAdoptionAppType {
    application: Application;
    applicationId: string;
    dateCreated: string;
    usage: number;
    users: User[];
}

interface UsageVsAdoptionType {
    applications: UsageVsAdoptionAppType[];
    name: string;
    newApps: string[];
    usage: number | null;
    adoption: number;
    startOfdayTimeStamp: string;
}

const columns: ColumnDef<UsageVsAdoptionType, string>[] = [
    {
        accessorKey: 'application.name',
        cell: AppNameLinkCell,
        enableColumnFilter: false,
        enableGlobalFilter: true,
        header: Header,
        id: 'apps',
        name: 'Common.application',
        size: 300,
        testId: 'application.name'
    },
    {
        accessorKey: 'dateCreated',
        cell: (props) => <DateFormatCell {...props} noTimeString />,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'dateCreated',
        name: 'Common.dateDiscovered',
        size: 100,
        testId: 'dateCreated'
    },
    {
        accessorKey: 'usage',
        cell: HMSCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'usage',
        name: 'Common.usage',
        size: 175,
        testId: 'usage'
    },
    {
        accessorKey: 'users',
        cell: (props) => <UserBubblesCell {...props} dataIsUser />,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'users',
        name: 'Common.users',
        size: 100,
        testId: 'users'
    }
];

export const NewAppBreakdownTable = ({
    reportData
}: {
    reportData: UsageVsAdoptionType[];
}): React.ReactElement => {
    const data = ((Array.isArray(reportData) && reportData) || []).flatMap(
        (day: UsageVsAdoptionType) =>
            day.applications?.map((app: UsageVsAdoptionAppType) => ({
                application: app.application,
                applicationId: app.applicationId,
                dateCreated: app.dateCreated,
                usage: app.usage,
                users: app.users
            }))
    );

    return (
        <ManagedTableV2ContextProvider
            columns={columns}
            ignoreSearchParams
            getRowId="applicationId"
            rows={data}
            mode="client"
            searchEnabled
            sortDirection="ASC"
            sortKey="dateCreated"
            tableName={TABLENAMES.USAGE_VS_ADOPTION}
        >
            <Card sx={{ marginBottom: '32px' }}>
                {/*
                    border radius fixes visual bug where the table doesn't have rounded corners
                    and causes a break in the bottom corners of the Card's border that contains it
                    TODO: Maybe there's a better way to fix this???
                */}
                <Box sx={{ borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                    <ManagedTableV2UI
                        renderFooter
                        titleDef={{
                            titleKey: 'Common.applications'
                        }}
                    />
                </Box>
            </Card>
        </ManagedTableV2ContextProvider>
    );
};

const NewAppBreakdown = ({
    reportData
}: {
    reportData: UsageVsAdoptionType[];
}) => {
    const { t } = useTranslation();
    return (
        <Box mt="32px" width="100%">
            <Typography variant="h4">
                {t('Reports.UsageReport.applicationsDiscovered')}
            </Typography>
            <Divider
                sx={{
                    borderBottomWidth: '4px',
                    borderColor: 'border.darkerDivider',
                    marginBottom: '16px',
                    marginTop: '8px'
                }}
            />
            <Typography paddingBottom="24px">
                {t('Reports.UsageReport.usageAdoption.table.subtitle')}
            </Typography>
            <NewAppBreakdownTable reportData={reportData} />
        </Box>
    );
};

export const UsageVsAdoption = ({
    reportData
}: {
    reportData: UsageVsAdoptionType[];
}) => {
    const { t } = useTranslation();
    const { palette } = useTheme();

    // Plot that goes in the admin hub, applications dashboard and in the organizational usage report
    return (
        <Box sx={{ marginY: '24px' }}>
            <Typography variant="h5">
                {t('Reports.UsageReport.adoption_and_usage')}
            </Typography>
            <Divider
                sx={{
                    borderBottomWidth: '4px',
                    borderColor: 'border.darkerDivider',
                    marginBottom: '16px',
                    marginTop: '8px'
                }}
            />
            <Typography paddingBottom="24px">
                {t('Reports.organizationalReport.vsAdoption.description')}
            </Typography>
            <ResponsiveContainer height={300} width="100%">
                <ComposedChart
                    data={reportData}
                    margin={{ bottom: 0, left: 0, right: 35, top: 20 }}
                    width={800}
                    height={300}
                >
                    <XAxis
                        dataKey="name"
                        axisLine={false}
                        angle={-40}
                        dy={15}
                        height={70}
                    />
                    <YAxis
                        allowDecimals={false}
                        yAxisId="left"
                        orientation="left"
                        tickCount={10}
                        tickFormatter={(value) => `${Math.ceil(value)}`}
                        label={{
                            angle: -90,
                            position: 'center',
                            style: { fill: palette.text.primary },
                            value: t('Reports.UsageReport.applications')
                        }}
                    />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickFormatter={hmsFormatter}
                        label={{
                            angle: 90,
                            dx: 45,
                            position: 'center',
                            style: { fill: palette.text.primary },
                            value: t('Reports.UsageReport.usageHours')
                        }}
                    />
                    <Tooltip
                        formatter={(value, name) => {
                            if (
                                name ===
                                t('Reports.UsageReport.averageUsagePerUser')
                            ) {
                                return [hmsFormatter(value as number), name];
                            }
                            return [value, name];
                        }}
                    />
                    <Legend />
                    <CartesianGrid
                        stroke={palette.chart.axis}
                        strokeDasharray="3 3"
                    />
                    <Bar
                        name={t('Reports.UsageReport.averageUsagePerUser')}
                        yAxisId="right"
                        dataKey="usage"
                        barSize={40}
                        fill={palette.chart.bar}
                    />
                    <Line
                        name={t('Reports.UsageReport.applicationsDiscovered')}
                        yAxisId="left"
                        type="linear"
                        dataKey="adoption"
                        stroke={palette.chart.line}
                        strokeWidth={3}
                        dot={{ strokeWidth: 5 }}
                    />
                </ComposedChart>
            </ResponsiveContainer>
            <NewAppBreakdown reportData={reportData} />
        </Box>
    );
};
